import { useMemo } from 'react'

import type { EChartsOption } from 'echarts'

import { GRID_SHARED } from '../historyGraph/constants'
import { defaultPalette, isHatchColor, resolveColor, resolveLabel } from '../historyGraph/palette'
import type { LaneRowData, LaneSectionData, ZoomRange } from '../historyGraph/types'

/** Row height in pixels */
const ROW_HEIGHT = 28
/** Section header row height (thinner) */
const _HEADER_ROW_HEIGHT = 18
/** Y-axis label area width */
const LABEL_WIDTH = 200

interface TimelineCategoryItem {
  label: string
  key: string
  section: string
  colorMapKey: string
  row: LaneRowData
  isSectionHeader: boolean
}

/**
 * Flatten sections into category items (reversed for ECharts bottom-to-top y-axis).
 * Filters out rows whose key is in hiddenRows.
 * When a section header is hidden, all rows in that section are hidden.
 */
function buildCategories(sections: LaneSectionData[], hiddenRows: Set<string>): TimelineCategoryItem[] {
  const items: TimelineCategoryItem[] = []
  for (const section of sections) {
    // If the section header itself is hidden, skip the entire section
    const headerKey = section.rows[0]?.key
    if (headerKey && hiddenRows.has(headerKey)) continue

    for (const row of section.rows) {
      if (hiddenRows.has(row.key)) continue
      items.push({
        label: row.label,
        key: row.key,
        section: section.title,
        colorMapKey: row.colorMapKey,
        row,
        isSectionHeader: row.colorMapKey === 'sectionHeader' || row.colorMapKey === 'sectionHeaderBlank',
      })
    }
  }
  return items.reverse()
}

/** Strip -hatch suffix for ECharts fill color */
function resolveEChartsColor(color: string): string {
  if (isHatchColor(color)) {
    return color.slice(0, -'-hatch'.length)
  }
  return color
}

/** Enum colorMapKeys where text labels should be shown inside bars */
const ENUM_KEYS = new Set(['powerMode', 'airControl'])

/** ColorMapKeys where the value field carries the actual color */
const VALUE_IS_COLOR_KEYS = new Set(['tempGradient', 'geofenceColor'])

export function useTimelineChartOptions(
  sections: LaneSectionData[],
  periodWindow: ZoomRange,
  hiddenRows: Set<string> = new Set()
): { option: EChartsOption; height: number } {
  return useMemo(() => {
    const palette = defaultPalette
    const categories = buildCategories(sections, hiddenRows)
    // Use unique keys for the category axis to avoid duplicate label collisions
    const categoryKeys = categories.map((c) => c.key)
    const totalRows = categories.length
    const chartHeight = totalRows * ROW_HEIGHT + 60

    // Map key → category index for fast lookups
    const keyToIdx = new Map(categoryKeys.map((k, i) => [k, i]))

    // Build rich-text styles + formatted y-axis labels
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const richStyles: Record<string, any> = {
      row: {
        fontSize: 10,
        color: '#6b7280',
      },
      dismiss: {
        fontSize: 11,
        color: '#9ca3af',
        padding: [0, 0, 0, 4],
      },
      sectionLabel: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#374151',
      },
      sectionDismiss: {
        fontSize: 12,
        color: '#9ca3af',
        padding: [0, 0, 0, 6],
      },
    }
    const formattedLabels: Record<string, string> = {}
    for (const cat of categories) {
      if (cat.colorMapKey === 'sectionHeaderBlank') {
        formattedLabels[cat.key] = ' '
      } else if (cat.isSectionHeader) {
        formattedLabels[cat.key] = `{sectionLabel|${cat.label}} {sectionDismiss|\u00d7}`
      } else {
        formattedLabels[cat.key] = `{row|${cat.label}} {dismiss|\u00d7}`
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allSeries: any[] = []

    // Data series per category row
    for (const cat of categories) {
      const { row, colorMapKey } = cat
      const isSectionHeader = cat.isSectionHeader
      const showLabels = !isSectionHeader && ENUM_KEYS.has(colorMapKey)
      const valueIsColor = !isSectionHeader && VALUE_IS_COLOR_KEYS.has(colorMapKey)
      const catIdx = keyToIdx.get(cat.key) ?? 0

      // data: [startTime, categoryIndex, endTime, stateValue, numericValue]
      allSeries.push({
        type: 'custom',
        name: cat.key,
        xAxisIndex: 0,
        yAxisIndex: 0,
        clip: true,
        silent: isSectionHeader,
        data: isSectionHeader
          ? [[periodWindow[0], catIdx, periodWindow[1], 'sectionHeader', null]]
          : row.intervals.map((iv) => [iv.start, catIdx, iv.end, iv.value, iv.numericValue ?? null]),
        renderItem: (
          _params: unknown,
          api: {
            coord: (v: [number, number]) => [number, number]
            value: (idx: number) => number | string
            size: (v: [number, number]) => [number, number]
          }
        ) => {
          const categoryIndex = api.value(1) as number
          const startX = api.coord([api.value(0) as number, categoryIndex])[0]
          const endX = api.coord([api.value(2) as number, categoryIndex])[0]
          const y = api.coord([0, categoryIndex])[1]
          const fullBarHeight = api.size([0, 1])[1] * 0.8
          const barWidth = Math.max(endX - startX, 1)

          // Section header: thin separator bar
          if (isSectionHeader) {
            return {
              type: 'rect',
              shape: { x: startX, y: y - 1.5, width: barWidth, height: 3 },
              style: { fill: '#9ca3af' },
            }
          }

          const stateValue = api.value(3) as string
          const numericValue = api.value(4) as number | undefined

          // For tempGradient and geofenceColor, the value IS the color
          let color: string
          if (valueIsColor) {
            color = stateValue
          } else {
            const rawColor = resolveColor(palette, colorMapKey, stateValue, numericValue)
            color = resolveEChartsColor(rawColor)
          }
          const rawColor = valueIsColor ? stateValue : resolveColor(palette, colorMapKey, stateValue, numericValue)
          const hasHatch = isHatchColor(rawColor)

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const children: any[] = [
            {
              type: 'rect',
              shape: {
                x: startX,
                y: y - fullBarHeight / 2,
                width: barWidth,
                height: fullBarHeight,
              },
              style: { fill: color },
            },
          ]

          // Hatch lines
          if (hasHatch) {
            const lineSpacing = 6
            const bx = startX
            const by = y - fullBarHeight / 2
            for (let offset = -fullBarHeight; offset < barWidth + fullBarHeight; offset += lineSpacing) {
              const sx = bx + offset
              const ex = sx + fullBarHeight
              children.push({
                type: 'line',
                shape: {
                  x1: Math.max(bx, sx),
                  y1: Math.max(by, by + Math.max(bx, sx) - sx),
                  x2: Math.min(bx + barWidth, ex),
                  y2: Math.min(by + fullBarHeight, by + Math.min(bx + barWidth, ex) - sx),
                },
                style: { stroke: 'rgba(255,255,255,0.45)', lineWidth: 1.5 },
                silent: true,
              })
            }
          }

          // Text label inside bar for enum rows
          if (showLabels && barWidth > 40) {
            const label = resolveLabel(palette, colorMapKey, stateValue, numericValue ?? undefined)
            children.push({
              type: 'text',
              x: startX + 4,
              y,
              style: {
                text: label,
                fontSize: 9,
                fill: '#fff',
                textVerticalAlign: 'middle',
              },
            })
          }

          return { type: 'group', children }
        },
        tooltip: {
          formatter: (params: { value: (string | number | null)[] }) => {
            const v = params.value
            const stateValue = v[3] as string
            const numericValue = v[4] as number | undefined
            // Use custom tooltip from the interval if available
            const iv = row.intervals.find((i) => i.start === (v[0] as number) && i.end === (v[2] as number))
            if (iv?.tooltip) {
              return iv.tooltip
            }
            const displayLabel = resolveLabel(palette, colorMapKey, stateValue, numericValue ?? undefined)
            const start = new Date(v[0] as number).toLocaleTimeString()
            const end = new Date(v[2] as number).toLocaleTimeString()
            return `<b>${cat.label}</b>: ${displayLabel}<br/>${start} \u2013 ${end}`
          },
        },
        z: 1,
      })
    }

    const option: EChartsOption = {
      animation: false,
      grid: [
        {
          ...GRID_SHARED,
          left: LABEL_WIDTH,
          top: 10,
          bottom: 30,
          borderWidth: 1,
          borderColor: '#d1d5db',
          show: true,
        },
      ],
      xAxis: [
        {
          type: 'time',
          gridIndex: 0,
          min: periodWindow[0],
          max: periodWindow[1],
          axisLabel: { show: true, fontSize: 10 },
          axisTick: { show: true },
          axisLine: { show: true },
          splitLine: {
            show: true,
            lineStyle: { color: '#d1d5db', opacity: 0.5 },
          },
          minInterval: 3600000,
          maxInterval: 3600000,
        },
      ],
      yAxis: [
        {
          type: 'category',
          gridIndex: 0,
          data: categoryKeys,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            fontSize: 10,
            color: '#6b7280',
            width: LABEL_WIDTH - 10,
            overflow: 'truncate',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter: ((value: string) => formattedLabels[value] ?? value) as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rich: richStyles as any,
          },
          triggerEvent: true,
          splitLine: { show: false },
        },
      ],
      tooltip: {
        trigger: 'item',
        confine: true,
      },
      series: allSeries,
    }

    return { option, height: chartHeight }
  }, [sections, periodWindow, hiddenRows])
}
